import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { createSession, getMyWards, type ActiveSessionConflictDto, type WardDto } from "~/lib/api";

type ShiftType = "day" | "night";

const SHIFT_OPTIONS: { value: ShiftType; label: string }[] = [
  { value: "day", label: "Day shift" },
  { value: "night", label: "Night shift" },
];

function shiftLabel(shift: ShiftType): string {
  return SHIFT_OPTIONS.find((option) => option.value === shift)?.label ?? shift;
}

function formatStarted(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

// The wards request resolves into one of these. The empty case is modelled as a
// `ready` state with no wards, so the empty-state copy and the picker share a
// branch and can't disagree about whether loading has finished.
type WardsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; wards: WardDto[] };

// The 409 summary, paired with the ward/shift the doctor picked so the modal can
// name them ("…on Oncology (Day shift)") and the force-retry can re-POST the same
// ward without re-reading live selection state (which could drift or be reset).
type Conflict = ActiveSessionConflictDto & {
  wardId: string;
  wardName: string;
  shiftType: ShiftType;
};

export default function SelectWardRoute(): JSX.Element {
  const navigate = useNavigate();

  const [wardsState, setWardsState] = useState<WardsState>({ status: "loading" });
  const [selectedWardId, setSelectedWardId] = useState<string | null>(null);
  const [shift, setShift] = useState<ShiftType | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [forcing, setForcing] = useState(false);

  const loadWards = useCallback(async (signal: { cancelled: boolean }): Promise<void> => {
    setWardsState({ status: "loading" });
    try {
      const result = await getMyWards();
      if (signal.cancelled) return;
      if (result.error || !result.data) {
        setWardsState({
          status: "error",
          message: `Couldn't load your wards (API responded with ${result.response.status}).`,
        });
        return;
      }
      setWardsState({ status: "ready", wards: result.data });
    } catch (err) {
      if (signal.cancelled) return;
      setWardsState({
        status: "error",
        message: err instanceof Error ? err.message : "Couldn't reach the server.",
      });
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    void loadWards(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadWards]);

  const goToSession = (id: string): void => {
    navigate(`/session/${id}`);
  };

  const onContinue = async (): Promise<void> => {
    if (!selectedWardId || !shift || submitting) return;

    const ward =
      wardsState.status === "ready"
        ? wardsState.wards.find((w) => w.id === selectedWardId)
        : undefined;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await createSession({ body: { wardId: selectedWardId, shiftType: shift } });

      // 409: another doctor already has an unfinished handover on this ward.
      // Surface its summary so the user can resume it or force a fresh start.
      // Branch on the status alone — if the body is missing the session id we
      // can't offer resume/force, so fall back to a plain error rather than a
      // dead-end "creation failed" message.
      if (result.response.status === 409) {
        const summary = (result.error ?? {}) as Partial<ActiveSessionConflictDto>;
        if (summary.existingSessionId) {
          setConflict({
            existingSessionId: summary.existingSessionId,
            outgoingDoctorName: summary.outgoingDoctorName ?? "another doctor",
            startedAt: summary.startedAt ?? "",
            patientCount: summary.patientCount ?? 0,
            wardId: selectedWardId,
            wardName: ward?.name ?? "this ward",
            shiftType: shift,
          });
          return;
        }
        setSubmitError("A handover is already in progress for this ward. Refresh and try again.");
        return;
      }
      if (result.error || !result.data) {
        setSubmitError(
          `Couldn't start the session (API responded with ${result.response.status}).`,
        );
        return;
      }
      goToSession(result.data.id);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Couldn't reach the server.");
    } finally {
      setSubmitting(false);
    }
  };

  const onStartNew = async (): Promise<void> => {
    if (!conflict || forcing) return;
    setForcing(true);
    setSubmitError(null);
    try {
      const result = await createSession({
        body: { wardId: conflict.wardId, shiftType: conflict.shiftType, force: true },
      });
      if (result.error || !result.data) {
        setConflict(null);
        setSubmitError(
          `Couldn't start a new session (API responded with ${result.response.status}).`,
        );
        return;
      }
      goToSession(result.data.id);
    } catch (err) {
      setConflict(null);
      setSubmitError(err instanceof Error ? err.message : "Couldn't reach the server.");
    } finally {
      setForcing(false);
    }
  };

  return (
    <section className="mx-auto w-full max-w-xl space-y-6 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Start a handover</h1>
        <p className="text-sm text-muted-foreground">
          Pick your ward and shift to begin or join today&apos;s handover.
        </p>
      </header>

      {wardsState.status === "loading" && (
        <div className="flex items-center justify-center py-12" aria-live="polite">
          <Loader2
            className="h-6 w-6 animate-spin text-muted-foreground"
            aria-label="Loading wards"
          />
        </div>
      )}

      {wardsState.status === "error" && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{wardsState.message}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void loadWards({ cancelled: false });
              }}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {wardsState.status === "ready" && wardsState.wards.length === 0 && (
        <Card>
          <CardContent className="space-y-2 py-8 text-center">
            <h2 className="text-base font-medium">You&apos;re not on any wards yet</h2>
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
              An administrator needs to add you to a ward before you can start a handover. Once
              you&apos;ve been added, your wards will appear here.
            </p>
          </CardContent>
        </Card>
      )}

      {wardsState.status === "ready" && wardsState.wards.length > 0 && (
        <div className="space-y-6">
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Ward</legend>
            <RadioGroup
              value={selectedWardId ?? ""}
              onValueChange={(value) => {
                setSelectedWardId(value);
                setSubmitError(null);
              }}
              className="gap-2"
            >
              {wardsState.wards.map((ward) => (
                <Label
                  key={ward.id}
                  htmlFor={`ward-${ward.id}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent has-[:checked]:border-primary has-[:checked]:bg-accent"
                >
                  <RadioGroupItem value={ward.id} id={`ward-${ward.id}`} />
                  <span className="flex flex-1 items-center justify-between gap-2">
                    <span className="font-medium">{ward.name}</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                      {ward.shortCode}
                    </span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Shift</legend>
            <RadioGroup
              value={shift ?? ""}
              onValueChange={(value) => {
                setShift(value as ShiftType);
                setSubmitError(null);
              }}
              className="grid grid-cols-2 gap-2"
            >
              {SHIFT_OPTIONS.map((option) => (
                <Label
                  key={option.value}
                  htmlFor={`shift-${option.value}`}
                  className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-border p-4 font-medium transition-colors hover:bg-accent has-[:checked]:border-primary has-[:checked]:bg-accent"
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`shift-${option.value}`}
                    className="sr-only"
                  />
                  {option.label}
                </Label>
              ))}
            </RadioGroup>
          </fieldset>

          {submitError && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="button"
            className="w-full"
            disabled={!selectedWardId || !shift || submitting}
            onClick={() => {
              void onContinue();
            }}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Continue
          </Button>
        </div>
      )}

      <Dialog
        open={conflict !== null}
        onOpenChange={(open) => {
          if (!open && !forcing) setConflict(null);
        }}
      >
        <DialogContent>
          {conflict && (
            <>
              <DialogHeader>
                <DialogTitle>Handover already in progress</DialogTitle>
                <DialogDescription>
                  There is already an active handover session on {conflict.wardName} (
                  {shiftLabel(conflict.shiftType)}).
                </DialogDescription>
              </DialogHeader>

              <dl className="space-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Outgoing doctor</dt>
                  <dd className="font-medium">{conflict.outgoingDoctorName}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Started</dt>
                  <dd className="font-medium">{formatStarted(conflict.startedAt)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Patients</dt>
                  <dd className="font-medium">{conflict.patientCount}</dd>
                </div>
              </dl>

              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  variant="outline"
                  disabled={forcing}
                  onClick={() => {
                    void onStartNew();
                  }}
                >
                  {forcing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                  Start a new session
                </Button>
                <Button
                  disabled={forcing}
                  onClick={() => {
                    goToSession(conflict.existingSessionId);
                  }}
                >
                  Resume existing session
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
